import { useCallback, useEffect, useState } from "react";
import { useData } from "../../context/DataContext";
import {
  getCustomSchedules,
  createCustomSchedule,
  updateCustomSchedule,
  deleteCustomSchedule,
} from "../../api/customSchedule";
import "bootstrap/dist/css/bootstrap.rtl.min.css";

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function employeeLabel(emp) {
  const name = emp?.name || emp?.fullName || "بدون اسم";
  const code = emp?.code || emp?.employeeCode || "";
  return code ? `${name} (${code})` : name;
}

export default function CustomSchedule() {
  const { employeesByBranch } = useData();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const allEmployees = Object.values(employeesByBranch || {})
    .flat()
    .filter((e) => e && e.id && e.status !== "terminated");

  const [form, setForm] = useState({
    employeeId: "",
    officialStartTime: "12:00",
    officialEndTime: "",
    workingHoursPerDay: 8,
    expectedPunchCount: "",
    notes: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getCustomSchedules();
      setSchedules(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل المواعيد المخصصة"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setForm({
      employeeId: "",
      officialStartTime: "12:00",
      officialEndTime: "",
      workingHoursPerDay: 8,
      expectedPunchCount: "",
      notes: "",
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (schedule) => {
    setForm({
      employeeId: schedule.employeeId,
      officialStartTime: schedule.officialStartTime,
      officialEndTime: schedule.officialEndTime || "",
      workingHoursPerDay: schedule.workingHoursPerDay,
      expectedPunchCount:
        schedule.expectedPunchCount === 2 || schedule.expectedPunchCount === 4
          ? String(schedule.expectedPunchCount)
          : "",
      notes: schedule.notes || "",
    });
    setEditingId(schedule.employeeId);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      const punchCount =
        form.expectedPunchCount === "2" || form.expectedPunchCount === "4"
          ? Number(form.expectedPunchCount)
          : null;
      const payload = {
        officialStartTime: form.officialStartTime,
        officialEndTime: form.officialEndTime || undefined,
        workingHoursPerDay: Number(form.workingHoursPerDay),
        expectedPunchCount: punchCount,
        notes: form.notes || undefined,
      };
      if (editingId) {
        await updateCustomSchedule(editingId, payload);
        setSuccess("تم تحديث الجدول المخصص بنجاح");
      } else {
        await createCustomSchedule({
          employeeId: form.employeeId,
          ...payload,
        });
        setSuccess("تم إنشاء الجدول المخصص بنجاح");
      }
      resetForm();
      load();
    } catch (err) {
      setError(getApiErrorMessage(err, "حدث خطأ أثناء الحفظ"));
    }
  };

  const handleDelete = async (employeeId, employeeName) => {
    if (!window.confirm(`هل أنت متأكد من حذف الجدول المخصص لـ ${employeeName}؟`))
      return;

    setError("");
    setSuccess("");
    try {
      await deleteCustomSchedule(employeeId);
      setSuccess("تم حذف الجدول المخصص بنجاح");
      load();
    } catch (err) {
      setError(getApiErrorMessage(err, "حدث خطأ أثناء الحذف"));
    }
  };

  const handleToggleActive = async (schedule) => {
    setError("");
    setSuccess("");
    try {
      await updateCustomSchedule(schedule.employeeId, {
        isActive: !schedule.isActive,
      });
      setSuccess(
        schedule.isActive
          ? "تم تعطيل الجدول المخصص"
          : "تم تفعيل الجدول المخصص"
      );
      load();
    } catch (err) {
      setError(getApiErrorMessage(err, "حدث خطأ"));
    }
  };

  const usedEmployeeIds = new Set(schedules.map((s) => s.employeeId));
  const availableEmployees = allEmployees.filter(
    (emp) => !usedEmployeeIds.has(emp.id)
  );

  return (
    <div className="container-fluid py-3" dir="rtl">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">المواعيد المخصصة للموظفين</h4>
          <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
            نافذة مرنة: يبصم من بداية الدوام الطبيعي حتى الوقت الذي تحدده HR،
            وتُحسب ساعات العمل المطلوبة من وقت البصمة. بعد الموعد يُطبَّق سماح
            اللائحة ثم التأخير حسب لائحة التأخيرات. يمكن فرض 4 بصمات مع بريك
            لموظفي المخزن (مثل 9–6).
          </p>
        </div>
        {!showForm && (
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            + إضافة جدول مخصص
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          />
        </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible" role="alert">
          {success}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccess("")}
          />
        </div>
      )}

      {showForm && (
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <strong>
              {editingId ? "تعديل جدول مخصص" : "إنشاء جدول مخصص جديد"}
            </strong>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={resetForm}
            >
              إلغاء
            </button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                {!editingId && (
                  <div className="col-md-6">
                    <label className="form-label">الموظف</label>
                    <select
                      className="form-select"
                      value={form.employeeId}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, employeeId: e.target.value }))
                      }
                      required
                    >
                      <option value="">اختر موظف...</option>
                      {availableEmployees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {employeeLabel(emp)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="col-md-3">
                  <label className="form-label">مسموح الحضور حتى</label>
                  <input
                    type="time"
                    className="form-control"
                    value={form.officialStartTime}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        officialStartTime: e.target.value,
                      }))
                    }
                    required
                  />
                  <small className="text-muted">
                    من بداية الدوام الطبيعي حتى هذا الوقت بدون تأخير
                  </small>
                </div>
                <div className="col-md-3">
                  <label className="form-label">
                    نهاية الدوام (اختياري للعرض)
                  </label>
                  <input
                    type="time"
                    className="form-control"
                    value={form.officialEndTime}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        officialEndTime: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">ساعات العمل المطلوبة</label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="24"
                    step="0.5"
                    value={form.workingHoursPerDay}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        workingHoursPerDay: e.target.value,
                      }))
                    }
                    required
                  />
                  <small className="text-muted">
                    تُحسب من وقت بصمة الحضور الفعلي
                  </small>
                </div>
                <div className="col-md-3">
                  <label className="form-label">عدد البصمات</label>
                  <select
                    className="form-select"
                    value={form.expectedPunchCount}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        expectedPunchCount: e.target.value,
                      }))
                    }
                  >
                    <option value="">افتراضي حسب التصنيف</option>
                    <option value="2">بصمتان (حضور / انصراف)</option>
                    <option value="4">4 بصمات (مع بريك)</option>
                  </select>
                  <small className="text-muted">
                    للمخزن ببريك اختر 4 بصمات (مثل 9–6 / 8 ساعات)
                  </small>
                </div>
                <div className="col-md-6">
                  <label className="form-label">ملاحظات</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.notes}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, notes: e.target.value }))
                    }
                    placeholder="سبب المواعيد المخصصة..."
                  />
                </div>
              </div>
              <div className="mt-3">
                <button type="submit" className="btn btn-success me-2">
                  {editingId ? "حفظ التعديلات" : "إنشاء"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
          <p className="mt-2">جاري التحميل...</p>
        </div>
      ) : schedules.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <p>لا توجد مواعيد مخصصة حالياً</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>الموظف</th>
                <th>القسم</th>
                <th>مسموح حتى</th>
                <th>نهاية الدوام</th>
                <th>ساعات العمل</th>
                <th>البصمات</th>
                <th>الحالة</th>
                <th>ملاحظات</th>
                <th>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((s) => (
                <tr
                  key={s.id}
                  className={!s.isActive ? "table-secondary" : ""}
                >
                  <td>
                    <strong>{s.employee?.fullName}</strong>
                    <br />
                    <small className="text-muted">
                      {s.employee?.employeeCode}
                    </small>
                  </td>
                  <td>{s.employee?.department?.name || "—"}</td>
                  <td>
                    <span className="badge bg-info text-dark">
                      {s.officialStartTime}
                    </span>
                  </td>
                  <td>{s.officialEndTime || "—"}</td>
                  <td>{s.workingHoursPerDay} ساعة</td>
                  <td>
                    {s.expectedPunchCount === 4
                      ? "4 (مع بريك)"
                      : s.expectedPunchCount === 2
                        ? "2"
                        : "افتراضي"}
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        s.isActive ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {s.isActive ? "مفعّل" : "معطّل"}
                    </span>
                  </td>
                  <td>
                    <small>{s.notes || "—"}</small>
                  </td>
                  <td>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => handleEdit(s)}
                        title="تعديل"
                      >
                        تعديل
                      </button>
                      <button
                        className={`btn ${
                          s.isActive
                            ? "btn-outline-warning"
                            : "btn-outline-success"
                        }`}
                        onClick={() => handleToggleActive(s)}
                        title={s.isActive ? "تعطيل" : "تفعيل"}
                      >
                        {s.isActive ? "تعطيل" : "تفعيل"}
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        onClick={() =>
                          handleDelete(s.employeeId, s.employee?.fullName)
                        }
                        title="حذف"
                      >
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
